import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { FC } from 'react';
import { useHealthAndSafetyState } from '../../../../contexts/healthAndSafety.context';

const SafetyProtocolsDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedProtocol } = useHealthAndSafetyState();

  if (!selectedProtocol) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="safety-protocols-details-container"
      >
        <Typography>
          {translateText('administration.healthAndSafety.noData')}
        </Typography>
      </Box>
    );
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="safety-protocols-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {selectedProtocol.protocolName}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('common.description')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="protocol-name">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.protocolName'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedProtocol.protocolName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="protocol-category">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.category'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedProtocol.category}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="protocol-responsible">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.responsible'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedProtocol.responsiblePerson}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="protocol-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.healthAndSafety.columns.status')}
              </Typography>
              <Chip
                label={
                  selectedProtocol.isActive
                    ? translateText(
                        'administration.healthAndSafety.status.active'
                      )
                    : translateText(
                        'administration.healthAndSafety.status.inactive'
                      )
                }
                color={selectedProtocol.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="protocol-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.protocolId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedProtocol.id}
              </Typography>
            </Grid>
            {selectedProtocol.description && (
              <Grid item xs={12} data-testid="protocol-description">
                <Typography variant="caption" color="textSecondary">
                  {translateText('common.description')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedProtocol.description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {selectedProtocol.procedures &&
        selectedProtocol.procedures.length > 0 && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {translateText(
                  'administration.healthAndSafety.labels.procedures'
                )}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List data-testid="protocol-procedures">
                {selectedProtocol.procedures.map((procedure, index) => (
                  <ListItem key={index} sx={{ pl: 0 }}>
                    <ListItemText
                      primary={`${index + 1}. ${procedure}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.datesTimeline'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="protocol-last-updated"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.lastUpdated'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedProtocol.lastUpdated)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="protocol-next-review">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.nextReview'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedProtocol.nextReviewDate)}
              </Typography>
            </Grid>
            {selectedProtocol.lastTrainingDate && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="protocol-last-training"
              >
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.lastTrainingDate'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedProtocol.lastTrainingDate)}
                </Typography>
              </Grid>
            )}
            {selectedProtocol.nextTrainingDate && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="protocol-next-training"
              >
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.nextTrainingDate'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedProtocol.nextTrainingDate)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.trainingInformation'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="protocol-training-required"
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.healthAndSafety.columns.training'
                )}
              </Typography>
              <Chip
                label={
                  selectedProtocol.trainingRequired
                    ? translateText(
                        'administration.healthAndSafety.values.required'
                      )
                    : translateText(
                        'administration.healthAndSafety.values.notRequired'
                      )
                }
                color={selectedProtocol.trainingRequired ? 'info' : 'default'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedProtocol.attachments &&
        selectedProtocol.attachments.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {translateText(
                  'administration.healthAndSafety.labels.attachments'
                )}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List data-testid="protocol-attachments">
                {selectedProtocol.attachments.map((attachment, index) => (
                  <ListItem key={index} sx={{ pl: 0 }}>
                    <ListItemText
                      primary={attachment}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}
    </Box>
  );
};

export default SafetyProtocolsDetailsView;
